import axios from 'axios';
import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { functions as mainFunctions } from '@/plugins/firebase'; // Main Firebase Functions

const FB_API_URL = import.meta.env.VITE_FIREBASE_FUNCTIONS_BASE_URL;

/**
 * Get Firebase Auth Token Dynamically
 * @returns {Promise<string>} - Returns the Firebase Auth token
 */
const getAuthToken = async () => {
  const auth = getAuth();
  if (auth.currentUser) {
    return await auth.currentUser.getIdToken(); // Fetch new token dynamically
  }
  throw new Error('User not authenticated');
};

/**
 * Fetch client features
 * @param {boolean} useREST - Use REST API for Sinch; otherwise, use callable functions.
 * @returns {Promise<Array>} - List of features.
 */
export const getClientFeatures = async (useREST = false) => {
  try {
    const authToken = await getAuthToken(); // Get token dynamically
    if (useREST) {
      const response = await axios.get(`${FB_API_URL}/features`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      return response.data.documents.map((doc) => doc.fields); // Adjust mapping as needed
    } else {
      const getFeatures = httpsCallable(mainFunctions, 'getClientFeatures');
      const response = await getFeatures();
      return response.data.features;
    }
  } catch (error) {
    console.error('Error fetching client features:', error);
    throw new Error('Failed to fetch client features');
  }
};

/**
 * Update client feature status.
 * @param {string} featureId - The feature ID to update.
 * @param {boolean} isEnabled - Whether the feature is enabled.
 * @param {boolean} useREST - Use REST API for Sinch; otherwise, use callable functions.
 * @returns {Promise<Object>} - Updated feature details.
 */
export const updateClientFeatures = async (featureId, isEnabled, useREST = false) => {
  if (!featureId || typeof isEnabled !== 'boolean') {
    throw new Error('Invalid arguments: featureId and isEnabled are required.');
  }

  try {
    const authToken = await getAuthToken(); // Get token dynamically
    if (useREST) {
      const response = await axios.patch(
        `${FB_API_URL}/features/${featureId}`,
        { fields: { enabled: { booleanValue: isEnabled } } },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      return response.data;
    } else {
      const updateFeatures = httpsCallable(mainFunctions, 'updateClientFeatures');
      const response = await updateFeatures({ featureId, isEnabled });
      return response.data.updatedFeature;
    }
  } catch (error) {
    console.error('Error updating client features:', error);
    throw new Error('Failed to update client features');
  }
};